<template>
  <div class="container">
    <ReviewsCategoriesHeader
      v-show="!isChartFullScreen"
      :title="$t('message.reviews.categories.title')"
      @date-change="handleDateChange"
    >
      <ReviewsCategoriesFacilitiesDialogButton
        @selected="handleFacilitySelect"
      />
    </ReviewsCategoriesHeader>
    <ReviewsCategoriesCompanyData>
      <SelectForm />
    </ReviewsCategoriesCompanyData>

    <ReviewsCategoriesSummary v-if="!isChartFullScreen" :data="summary" />
    <ReviewsCategoriesHeatmap
      :title="$t('message.reviews.reviews')"
      :description="$t('message.reviews.categories.chartDescription')"
      :data="heatmap"
      @toggle-fullscreen="handleHeatmapFullscreenToggle"
    />
    <ReviewsCategoriesTabsContainer v-if="!isChartFullScreen" :tabs="tabs" />
    <SectionLoader :status="isLoading" />
  </div>
</template>

<script>
import { getHeatmapWithLabelColors } from "@/helpers/helpers";
import FILTER_TYPES from "@/enums/gtm-facility-tab";
import { GTM_EVENT_NAME } from "@/enums/gtm-events";
import ReviewsCategoriesHeatmap from "@/components/Reviews/Categories/ReviewsCategoriesHeatmap.vue";
import ReviewsCategoriesSummary from "@/components/Reviews/Categories/ReviewsCategoriesSummary.vue";
import SectionLoader from "@/components/Loader/SectionLoader.vue";
import { categoryAnalyticsCommonMixin } from "@/helpers/mixins/category-analytics-common";
import ReviewsCategoriesCompanyData from "@/components/Reviews/Categories/ReviewsCategoriesCompanyData";
import ReviewsCategoriesHeader from "@/components/Reviews/Categories/ReviewsCategoriesHeader.vue";
import ReviewsCategoriesFacilitiesDialogButton from "@/components/Reviews/Categories/ReviewsCategoriesFacilitiesDialogButton.vue";
import ReviewsCategoriesTabsContainer from "@/components/Reviews/Categories/ReviewsCategoriesTabsContainer.vue";
import cuid from "cuid";
import serviceHandler from "@/helpers/handlers/service";
import reviewBoosterService from "@/services/review-booster";
import SelectForm from "@/components/ReviewBooster/SelectForm";
import AnalyticsService from "@/services/analytics";
import { endpoints } from "@/config/endpoints";
const analyticsService = new AnalyticsService(endpoints.analytics.rb);

export default {
  name: "ReviewsCategories",
  components: {
    ReviewsCategoriesSummary,
    ReviewsCategoriesHeatmap,
    SectionLoader,
    ReviewsCategoriesCompanyData,
    ReviewsCategoriesHeader,
    ReviewsCategoriesFacilitiesDialogButton,
    ReviewsCategoriesTabsContainer,
    SelectForm
  },
  mixins: [categoryAnalyticsCommonMixin],
  data() {
    return {
      categorizationStatistics: null,
      isLoading: false,
      isChartFullScreen: false,
      reviewBooster: null,
      formList: null
    };
  },
  computed: {
    comparisonParams() {
      return {
        ...this.getComparisonParams,
        review_booster: this.$route.query.reviewBoosterKey
      };
    },
    basicParams() {
      return {
        ...this.getBasicParams,
        review_booster: this.$route.query.reviewBoosterKey
      };
    },

    tabs() {
      return [
        {
          id: cuid(),
          text: this.$t("message.reviews.categories.sentiment"),
          name: "sentiment_details",
          component: () =>
            import(
              "@/components/Reviews/Categories/ReviewsCategoriesColorTriadTable.vue"
            ),
          searchParamName: "q",
          fetchMethod: params =>
            analyticsService.getSentimentTable({
              ...params,
              review_booster: this.$route.query.reviewBoosterKey
            })
        },
        {
          id: cuid(),
          text: this.$t("message.reviews.categories.generalSentiment"),
          name: "sentiment_general",
          component: () =>
            import(
              "@/components/Reviews/Categories/ReviewsCategoriesColorScaleTable.vue"
            ),
          searchParamName: "q",
          fetchMethod: params =>
            analyticsService.getSentimentAggregatedTable({
              ...params,
              review_booster: this.$route.query.reviewBoosterKey
            })
        }
      ];
    }
  },
  methods: {
    async getReviewBoosterList() {
      this.isLoading = true;
      const { results } = await serviceHandler(
        reviewBoosterService.getReviewBoosterList()
      );
      this.formList = results;
      this.isLoading = false;
    },
    async fetchHeatmapData(payload) {
      this.heatmap.isLoading = true;
      const data = await analyticsService.getHeatmap(payload);
      this.heatmap.isLoading = false;
      return getHeatmapWithLabelColors(data);
    },

    async getPageData() {
      await this.fetchCoreData();
    },
    async fetchSummaryData(payload) {
      return analyticsService.getSummary(payload);
    },

    async handleDateChange(payload) {
      await this.changeDate(payload);
      const { date_from, date_to, period_type } = payload;
      this.$gtm.trackCustomEvent({
        event: GTM_EVENT_NAME.FILTER_DATE_PERIOD_V2,
        date_from,
        date_to,
        period_type
      });
    },
    handleHeatmapFullscreenToggle() {
      this.toggleHeatmapScreen();
      this.$gtm.trackCustomEvent({
        event:
          GTM_EVENT_NAME.REVIEW_CATEGORY_ANALYTICS_OVERVIEW_HEATMAP_FULLSCREEN
      });
    },
    async handleFacilitySelect(facility) {
      this.selectFacility(facility);
      this.$gtm.trackCustomEvent({
        event: GTM_EVENT_NAME.FILTER_LOCATIONS_V2,
        filter_type: FILTER_TYPES[facility.tab] || "all_locations",
        num_selected: 1
      });
    }
  },
  watch: {
    "$route.query.reviewBoosterKey": {
      async handler(currentKey) {
        if (!currentKey) return;
        await this.prepareInitialData();
      },
      immediate: true
    }
  }
};
</script>
<style lang="scss" scoped>
.company-control {
  display: flex;
  align-items: center;
  gap: $spacing * 3;
}
</style>
